(() => {
  'use strict';

  const ROW = '.collection-slider-row';
  const EXPANDED = '.product-block.expanded';
  const EPSILON = 0.5;
  /** Breathing room between the sticky header and the section's heading. */
  const HEADING_GAP = 24;

  function setPx(el, prop, value) {
    const prev = parseFloat(el.style.getPropertyValue(prop));
    if (Number.isFinite(prev) && Math.abs(prev - value) < EPSILON) return;
    el.style.setProperty(prop, `${value.toFixed(2)}px`);
  }

  /* ---- 1. card-below-photo offset (drives the panel lift) --------------- */

  const GRID_TEXT_GAP = 30;

  function textBottom(row) {
    let max = -Infinity;
    row.querySelectorAll('.product-block').forEach((card) => {
      card.querySelectorAll('.product-block__title, .product-block__subtitle, .product-price').forEach((el) => {
        if (el.closest('.quickbuy-container')) return;
        const r = el.getBoundingClientRect();
        if (r.height > 0 && r.bottom > max) max = r.bottom;
      });
    });
    return max;
  }

  function applyOffset(row) {
    const panel = row.querySelector('.quickbuy-container');
    if (!panel) return;
    const text = textBottom(row);
    if (!Number.isFinite(text)) return;
    const naturalTop = panel.getBoundingClientRect().top - (parseFloat(getComputedStyle(panel).marginTop) || 0);
    const target = text + GRID_TEXT_GAP;
    const rowTop = row.getBoundingClientRect().top;
    // Never push the panel DOWN past where it would sit unlifted.
    setPx(row, '--sp-qb-lift', Math.max(0, naturalTop - target));
    // The caret's base IS the panel's top edge, so it is published here from
    // the same measurement rather than from the photo.
    setPx(row, '--sp-qb-caret-y', Math.min(target, naturalTop) - rowTop);
  }

  /* ---- 2. caret position + fill (depends on WHICH card is open) --------- */

  function applyCaret(row) {
    const card = row.querySelector(EXPANDED);
    const photo = card && card.querySelector('.image-cont');
    if (!photo) return;

    const rowRect = row.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const photoRect = photo.getBoundingClientRect();
    if (!(cardRect.width > 0) || !(photoRect.height > 0)) return;

    setPx(row, '--sp-qb-caret-x', cardRect.left + cardRect.width / 2 - rowRect.left);
    // --sp-qb-caret-y is published by applyOffset (the panel's top edge).

    // Match the panel, not a theme variable: the caret's whole job is to read
    // as the panel's own edge, so whatever the panel actually paints is the
    // right answer. `.inner` is the fallback in case the fill ever moves onto
    // it; a transparent result is discarded so the CSS fallback survives.
    const panel = row.querySelector('.quickbuy-container');
    if (!panel) return;
    for (const el of [panel, panel.querySelector('.inner')]) {
      if (!el) continue;
      const bg = getComputedStyle(el).backgroundColor;
      if (bg && bg !== 'transparent' && !/rgba\(0,\s*0,\s*0,\s*0\)/.test(bg)) {
        row.style.setProperty('--sp-qb-caret-fill', bg);
        return;
      }
    }
  }

  /* ---- 3. switch floor --------------------------------------------------- */


  const pin = { panel: null, height: 0, mo: null, release: 0, safety: 0 };

  function clearPin() {
    if (pin.mo) { pin.mo.disconnect(); pin.mo = null; }
    if (pin.release) { clearTimeout(pin.release); pin.release = 0; }
    if (pin.safety) { clearTimeout(pin.safety); pin.safety = 0; }
    if (pin.panel) pin.panel.style.minHeight = '';
    pin.panel = null;
    pin.height = 0;
  }

  function beginSwitch(row) {
    const panel = row.querySelector('.quickbuy-container');
    if (!panel) return;
    const current = panel.getBoundingClientRect().height;
    if (current <= 4) return; // a fresh open — nothing open to hold up

    clearPin();
    pin.panel = panel;
    pin.height = current;

    // Pin WITHOUT transitioning. min-height is transitioned in CSS (that is
    // what makes the settle below smooth), but this first write must land
    // instantly: if it eased 0 -> 605 it would still be near zero when the
    // vendor writes the 160px shell a frame later, and the collapse would
    // happen anyway. Dropping min-height from transition-property for one
    // forced reflow leaves height and margin-top untouched, which matters
    // because the panel may still be animating when the switch is clicked.
    panel.style.transitionProperty = 'height, margin-top';
    panel.style.minHeight = `${current.toFixed(2)}px`;
    void panel.offsetHeight; // flush, so the un-transitioned value is adopted
    panel.style.transitionProperty = '';

    // Settle on the FIRST height the vendor writes once real content is in.
    // Keyed off `.quickbuy-content` rather than the after-open event because
    // the event and the ResizeObserver's write race each other; the content
    // being present is the fact that actually matters.
    pin.mo = new MutationObserver(() => {
      if (!pin.panel || !panel.querySelector('.quickbuy-content')) return;
      const target = parseFloat(panel.style.height);
      if (!Number.isFinite(target) || target <= 4) return;
      pin.mo.disconnect(); pin.mo = null;
      // Now transitioned: the floor eases from the outgoing height to the
      // incoming one. Taller and shorter both work — the box follows whichever
      // of height/min-height is larger, and both are heading to the same place.
      panel.style.minHeight = `${target.toFixed(2)}px`;
      if (pin.safety) { clearTimeout(pin.safety); pin.safety = 0; }
      // Drop the floor once it and height agree, so it cannot outlive the swap
      // and hold a closing panel open. No visual change at that point.
      pin.release = window.setTimeout(clearPin, transitionMs(panel) + 120);
    });
    pin.mo.observe(panel, { attributes: true, attributeFilter: ['style'] });

    // If the fetch never lands, do not leave a floor wedged under the panel.
    pin.safety = window.setTimeout(clearPin, 8000);
  }

  /** The panel's own open duration, so the release is never a guess. */
  function transitionMs(panel) {
    const first = getComputedStyle(panel).transitionDuration.split(',')[0];
    const ms = parseFloat(first) * 1000;
    return Number.isFinite(ms) && ms > 0 ? ms : 700;
  }

  /* ---- 4. scroll target ------------------------------------------------- */

  function stickyHeaderHeight() {
    try {
      if (window.theme && typeof theme.stickyHeaderHeight === 'function') {
        return theme.stickyHeaderHeight() || 0;
      }
    } catch (e) { /* fall through to the DOM probe */ }
    const header = document.querySelector('.header-section, .sticky-header, header');
    return header ? header.getBoundingClientRect().height : 0;
  }

  function correctScroll(row) {
    requestAnimationFrame(() => {
      const top = row.getBoundingClientRect().top + window.scrollY - stickyHeaderHeight() - HEADING_GAP;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    });
  }

  /* ---- wiring ----------------------------------------------------------- */

  function onOpen(evt) {
    const row = evt.target.closest ? evt.target.closest(ROW) : null;
    if (!row) return; // grid quick buy, not the carousel — vendor handles it
    if (evt.type === 'on:quickbuy:before-open') {
      // Order matters: read the outgoing height before anything else runs.
      beginSwitch(row);
      correctScroll(row);
    }
    applyOffset(row);
    applyCaret(row);
  }

  function init() {
    const rows = document.querySelectorAll(ROW);
    if (!rows.length) return;

    // `expanded` is already set by the time before-open fires (main.js toggles
    // the class first), so the caret can be placed before anything animates.
    document.addEventListener('on:quickbuy:before-open', onOpen);
    document.addEventListener('on:quickbuy:after-open', onOpen);

    // Drop the switch floor the instant a close begins. `on:quickbuy:after-close`
    // is the only close event the vendor fires and it lands AFTER the collapse,
    // by which point a floor would already have blocked it — so the close is
    // caught at the click instead, in the capture phase, ahead of main.js's own
    // delegated handler.
    document.addEventListener('click', (evt) => {
      const el = evt.target.closest && evt.target.closest('.close-detail');
      if (el && el.closest(ROW)) clearPin();
    }, true);
    document.addEventListener('on:quickbuy:after-close', clearPin);

    const ro = typeof ResizeObserver === 'function'
      ? new ResizeObserver((entries) => {
          for (const entry of entries) {
            const row = entry.target.closest(ROW);
            if (!row) continue;
            applyOffset(row);
            if (row.querySelector(EXPANDED)) applyCaret(row);
          }
        })
      : null;

    rows.forEach((row) => {
      applyOffset(row);
      // Watch the card, not the row — see the header note on loops. This also
      // covers the real settling cases: a lazy photo arriving, a webfont
      // swapping in and re-wrapping the title, and viewport resize, none of
      // which share a single event.
      const card = row.querySelector('.product-block');
      if (ro && card) ro.observe(card);
    });

    if (!ro) {
      window.addEventListener('resize', () => rows.forEach((row) => {
        applyOffset(row);
        if (row.querySelector(EXPANDED)) applyCaret(row);
      }), { passive: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
